import { apiClient } from './apiClient';

/**
 * Service for managing role translations from the database
 */
class RoleTranslationService {
  constructor() {
    this.cache = new Map(); // Cache: language -> { key: label, ... }
    this.loadingPromises = new Map(); // Track ongoing requests per language
  }

  /**
   * Get all role translations for a specific language
   * Uses cache to avoid duplicate requests
   * @param {string} language - Language code (fr, en, de, it)
   * @returns {Promise<Object>} Object with key-label pairs
   */
  async getTranslations(language) {
    // Return cached data if available
    if (this.cache.has(language)) {
      return this.cache.get(language);
    }

    // If a request is already in progress for this language, wait for it
    if (this.loadingPromises.has(language)) {
      return this.loadingPromises.get(language);
    }

    // Start new request
    const promise = apiClient
      .get(`/api/role-translations/${language}`)
      .then((translations) => {
        // Cache the result
        this.cache.set(language, translations);
        // Remove from loading promises
        this.loadingPromises.delete(language);
        return translations;
      })
      .catch((error) => {
        // Remove from loading promises on error
        this.loadingPromises.delete(language);
        console.error(`Error fetching role translations for ${language}:`, error);
        // Return empty object on error (fallback will use key)
        return {};
      });

    this.loadingPromises.set(language, promise);
    return promise;
  }

  /**
   * Get a specific role label for a key and language
   * @param {string} key - Translation key (e.g., "role-dentist")
   * @param {string} language - Language code
   * @returns {Promise<string>} Translated label or key if not found
   */
  async getLabel(key, language) {
    try {
      const translations = await this.getTranslations(language);
      return translations[key] || key; // Fallback to key if not found
    } catch (error) {
      console.error(`Error getting label for ${key} in ${language}:`, error);
      return key; // Fallback to key on error
    }
  }

  /**
   * Clear cache for a specific language or all languages
   * @param {string} language - Optional language code. If not provided, clears all cache
   */
  clearCache(language = null) {
    if (language) {
      this.cache.delete(language);
      this.loadingPromises.delete(language);
    } else {
      this.cache.clear();
      this.loadingPromises.clear();
    }
  }
}

// Export singleton instance
export const roleTranslationService = new RoleTranslationService();
export default roleTranslationService;

